import {APIClient} from '../utils/APIClient';
import {API_URL} from '../settings/local_settings';
import {uploadWithProgress} from '../utils/multipart';

// Capa API de la Economía del cliente (contrato de la tanda 2026-09-25, §3 y §5).
//
// Endpoints de agencia: responden `{status:'OK', data:…}`; un cliente o una línea ajenos vuelven
// como 404 uniforme y el módulo apagado como 403 `MODULE_NOT_AVAILABLE` en las escrituras. La
// sugerencia del modal de objetivo NUNCA es un error: sin módulo o sin perfil responde
// `available:false` con su motivo, porque el modal se abre igual.
//
// El resumen que dibuja la card de la ficha NO sale de acá: viaja en el bloque `economy` del
// agregado `client/<id>/panel/` (ver `clientPanelApi.js`). Esta capa la usa la pantalla de carga
// `/client/:id/economia`, que sí tiene endpoint propio.
const BASE = API_URL;

function clientBase(clientId) {
    return `${BASE}client/${encodeURIComponent(clientId)}/economics/`;
}

// El importador sube el MISMO archivo dos veces (vista previa y aplicar): el backend re-valida todo
// en el servidor y el `token` firmado de la vista previa ata las dos llamadas al mismo contenido.
function importFormData(file, extra = {}) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    Object.keys(extra).forEach((key) => {
        if (extra[key] !== undefined && extra[key] !== null) formData.append(key, String(extra[key]));
    });
    return formData;
}

export const ClientEconomicsApi = {
    /** Perfil vigente + líneas con su override + ticket calculado (§3.2). */
    getProfile(clientId) {
        return new APIClient(clientBase(clientId)).call('GET');
    },

    /** Nueva versión del perfil del cliente. Devuelve el mismo `data` del GET (§3.2). */
    saveProfile(clientId, body) {
        return new APIClient(clientBase(clientId)).call('POST', body);
    },

    /** Historia descendente, del cliente o de UNA línea (`business_unit_id`). */
    getVersions(clientId, businessUnitId = null) {
        const query = businessUnitId ? `?business_unit_id=${encodeURIComponent(businessUnitId)}` : '';
        return new APIClient(`${clientBase(clientId)}versions/${query}`).call('GET');
    },

    /** Override de una línea: mismo body que el perfil; lo que no venga se copia del cliente (§3.3). */
    saveLineOverride(clientId, businessUnitId, body) {
        return new APIClient(`${clientBase(clientId)}lines/${encodeURIComponent(businessUnitId)}/`)
            .call('POST', body);
    },

    /** La línea vuelve a heredar del cliente (desactiva el override vigente). */
    removeLineOverride(clientId, businessUnitId) {
        return new APIClient(`${clientBase(clientId)}lines/${encodeURIComponent(businessUnitId)}/`)
            .call('DELETE');
    },

    /** Lo que el modal de objetivo muestra como sugerencia (§3.4). */
    getSuggestion(businessUnitId) {
        return new APIClient(`${BASE}business-unit/${encodeURIComponent(businessUnitId)}/economics/suggestion/`)
            .call('GET');
    },

    /** Vista previa de la planilla: cada fila con su caso nombrado, sin escribir nada (§3.5). */
    importPreview(file) {
        return uploadWithProgress(`${BASE}agency/economics/import/preview/`, importFormData(file));
    },

    /**
     * Aplicar TODO o NADA. Con errores en la vista previa sólo avanza si el usuario lo pidió
     * explícito (`skipErrors`): el backend no saltea filas por su cuenta.
     */
    importApply(file, token, skipErrors = false) {
        return uploadWithProgress(`${BASE}agency/economics/import/apply/`,
            importFormData(file, {token, skip_errors: skipErrors ? 'true' : 'false'}));
    },

    // --- Escalafy (§5) ---------------------------------------------------------------------------

    escalafyAuthUrl(redirectUri, reconnectCredentialId = null) {
        const params = new URLSearchParams({redirect_uri: redirectUri});
        if (reconnectCredentialId) params.set('reconnect_credential_id', String(reconnectCredentialId));
        return new APIClient(`${BASE}agency/escalafy/auth-url/?${params.toString()}`).call('GET');
    },

    escalafyCallback({code, state, redirectUri}) {
        return new APIClient(`${BASE}agency/escalafy/callback/`)
            .call('POST', {code, state, redirect_uri: redirectUri});
    },

    /** Las organizaciones EN VIVO de una credencial: sirve para volver después de que suban el rol. */
    escalafyOrganizations(credentialId) {
        return new APIClient(`${BASE}agency/escalafy/organizations/?credential_id=${encodeURIComponent(credentialId)}`)
            .call('GET');
    },

    /** Las conexiones de la agencia; con `agencyClientId`, sólo las de ese cliente. */
    escalafyConnections(agencyClientId = null) {
        const query = agencyClientId ? `?agency_client_id=${encodeURIComponent(agencyClientId)}` : '';
        return new APIClient(`${BASE}agency/escalafy/connections/${query}`).call('GET');
    },

    escalafyLink({credentialId, orgId, agencyClientId, businessUnitId = null}) {
        const body = {credential_id: credentialId, org_id: orgId, agency_client_id: agencyClientId};
        if (businessUnitId) body.business_unit_id = businessUnitId;
        return new APIClient(`${BASE}agency/escalafy/connections/`).call('POST', body);
    },

    escalafyDisconnect(connectionId) {
        return new APIClient(`${BASE}agency/escalafy/connections/${encodeURIComponent(connectionId)}/`)
            .call('DELETE');
    },

    /**
     * «Usar los datos de Escalafy»: crea una versión ESCALAFY con lo último que derivó el sync,
     * vigente hoy (le gana a la corrección manual que la había tapado). 409 `NO_ESCALAFY_PROPOSAL`
     * o `MANUAL_VERSION_TODAY` cuando no se puede.
     */
    escalafyUse(connectionId) {
        return new APIClient(`${BASE}agency/escalafy/connections/${encodeURIComponent(connectionId)}/use/`)
            .call('POST');
    },

    escalafySync(connectionId) {
        return new APIClient(`${BASE}agency/escalafy/connections/${encodeURIComponent(connectionId)}/sync/`)
            .call('POST');
    },
};

export default ClientEconomicsApi;
